/**
 * Copilot.jsx
 * A brand brief in, a month of drafts out.
 *
 * Two deliberately separate decisions, in this order:
 *   Generate strategy  — one AI call. An objective, a content mix, and the
 *                        month's ideas. Writes nothing, schedules nothing.
 *   Generate the month — writes every caption and creates the drafts. Costs
 *                        more, creates thirty things, so it is confirmed.
 *
 * Nothing on this screen publishes. The drafts land in an ordinary campaign
 * and the approval gate in front of them is the one that has always been
 * there.
 *
 * Dependencies: services/api.js, components/copilot/*, hooks/useBrands.jsx
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api.js';
import { useBrands, useSelectedBrand } from '../hooks/useBrands.jsx';
import { Calendar, Layers, Sparkle, Table, Warning } from '../components/Icons.jsx';
import NoBrands from '../components/NoBrands.jsx';

import BrandBrief from '../components/copilot/BrandBrief.jsx';
import StrategySummary from '../components/copilot/StrategySummary.jsx';
import ContentPlan from '../components/copilot/ContentPlan.jsx';
import PostIdeas from '../components/copilot/PostIdeas.jsx';
import Assistant from '../components/copilot/Assistant.jsx';

const TABS = [
  { key: 'strategy', label: 'Strategy' },
  { key: 'ideas', label: 'Content ideas' },
  { key: 'calendar', label: 'Calendar' },
  { key: 'months', label: 'Saved months' },
];

const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function Copilot() {
  const navigate = useNavigate();
  const { brands, loading: brandsLoading, refresh: refreshBrands } = useBrands();
  const [brandId, setBrandId] = useSelectedBrand();

  const [month, setMonth] = useState(thisMonth);
  const [tab, setTab] = useState('strategy');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Two different kinds of busy, because they cost different amounts and the
  // second one creates things.
  const [planning, setPlanning] = useState(false);
  const [writing, setWriting] = useState(false);
  const [note, setNote] = useState(null);

  const load = useCallback(async () => {
    if (!brandId) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setData(await api.copilot.get({ brandId, month }));
      setError(null);
    } catch (e) {
      setError(e);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [brandId, month]);

  useEffect(() => {
    load();
  }, [load]);

  /** The cheap half. One call, no posts, nothing scheduled. */
  async function generateStrategy() {
    setPlanning(true);
    setNote(null);
    setError(null);
    try {
      const res = await api.copilot.strategy({ brandId, month });
      setNote(
        `Strategy ready — ${res.strategy.ideas.length} ideas, ${res.plan.length} of them placed this month. ` +
          `Cost about £${(res.costPence / 100).toFixed(2)}. Nothing has been written yet.`,
      );
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setPlanning(false);
    }
  }

  /**
   * The expensive half. Confirmed here as well as in the API, because thirty
   * drafts is a lot to arrive by accident.
   */
  async function generateMonth() {
    const strategy = data?.strategy;
    if (!strategy) return;

    const already = strategy.generatedAt
      ? '\n\nThis month has been generated once already. Doing it again creates a second set of drafts; the first set is left alone.'
      : '';

    const ok = window.confirm(
      `Write ${data.plan.length} posts for ${data.monthLabel}?\n\n` +
        'Copilot will write every caption and put each post in Drafts for approval. ' +
        'Nothing is scheduled and nothing is published.' +
        already,
    );
    if (!ok) return;

    setWriting(true);
    setNote(null);
    setError(null);
    try {
      const res = await api.copilot.month(strategy.id);
      setNote(`${res.message} Cost about £${(res.costPence / 100).toFixed(2)}.`);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setWriting(false);
    }
  }

  async function saveIdeas(ideas) {
    try {
      await api.copilot.saveIdeas(data.strategy.id, ideas);
      await load();
    } catch (e) {
      setError(e);
    }
  }

  if (!brandsLoading && brands.length === 0) return <NoBrands />;

  const strategy = data?.strategy ?? null;
  const monthLabel = data?.monthLabel ?? month;
  const brand = data?.brand ?? null;

  return (
    <>
      <div className="page-head page-head--hero">
        <div>
          <div className="page-head__eyebrow">Copilot</div>
          <h1>
            <span className="copilot-mark">
              <Sparkle size={26} />
            </span>
            KreeBoom Copilot <span className="badge badge--accent">Beta</span>
          </h1>
          <p>
            Tell Copilot about the business once. It proposes the month’s strategy, the posts to
            get there, and writes them into drafts for your approval.
          </p>
        </div>

        <div className="page-head__actions">
          {brands.length > 1 && (
            <select
              className="select"
              value={brandId}
              onChange={(e) => setBrandId(e.target.value)}
              aria-label="Brand"
            >
              {brands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          )}

          <button className="btn" onClick={() => setTab('months')}>
            <Table size={16} /> Saved months
          </button>

          <button
            className="btn btn--primary"
            onClick={generateStrategy}
            disabled={planning || !brandId || (data?.platforms?.length ?? 0) === 0}
          >
            <Sparkle size={16} />
            {planning ? 'Thinking…' : strategy ? 'Regenerate strategy' : 'Generate 30-day strategy'}
          </button>
        </div>
      </div>

      <div className="tab-row" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            type="button"
            aria-selected={tab === t.key}
            className={`tab${tab === t.key ? ' is-active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}

      {note && (
        <div className="notice notice--info">
          <Sparkle />
          <span>{note}</span>
        </div>
      )}

      {data?.platforms?.length === 0 && (
        <div className="notice notice--error">
          <Warning />
          <span>
            {brand?.name} has no connected accounts, so there is nothing to plan for.{' '}
            <button className="link-more" onClick={() => navigate('/brands')}>
              Connect Facebook or Instagram
            </button>
          </span>
        </div>
      )}

      {loading && !data ? (
        <section className="card">
          <p className="row__sub">Loading {monthLabel}…</p>
        </section>
      ) : (
        <>
          {tab === 'strategy' && brand && (
            <>
              <div className="copilot-grid copilot-grid--top">
                <BrandBrief
                  brand={brand}
                  platforms={data.platforms}
                  onSaved={async () => {
                    await refreshBrands();
                    await load();
                  }}
                  navigate={navigate}
                />
                <StrategySummary
                  strategy={strategy}
                  monthLabel={monthLabel}
                  generating={planning}
                  onGenerate={generateStrategy}
                />
                <Assistant
                  brandId={brandId}
                  brandName={brand.name}
                  month={monthLabel}
                  hasStrategy={!!strategy}
                />
              </div>

              {strategy && (
                <>
                  <GenerateBar
                    data={data}
                    writing={writing}
                    onGenerate={generateMonth}
                    navigate={navigate}
                  />

                  <div className="copilot-grid copilot-grid--bottom">
                    <ContentPlan
                      month={month}
                      monthLabel={monthLabel}
                      plan={data.plan}
                      posts={data.posts}
                      existing={data.existing}
                      formats={data.formats}
                      onMonth={setMonth}
                      onShowIdeas={() => setTab('ideas')}
                      navigate={navigate}
                    />
                    <PostIdeas
                      strategy={strategy}
                      formats={data.formats}
                      plan={data.plan}
                      posts={data.posts}
                      onRemove={saveIdeas}
                      compact
                      navigate={() => setTab('ideas')}
                    />
                  </div>
                </>
              )}
            </>
          )}

          {tab === 'ideas' && (
            <PostIdeas
              strategy={strategy}
              formats={data?.formats}
              plan={data?.plan}
              posts={data?.posts}
              onRemove={saveIdeas}
              navigate={navigate}
            />
          )}

          {tab === 'calendar' && (
            <ContentPlan
              month={month}
              monthLabel={monthLabel}
              plan={data?.plan ?? []}
              posts={data?.posts ?? []}
              existing={data?.existing ?? []}
              formats={data?.formats}
              onMonth={setMonth}
              onShowIdeas={() => setTab('ideas')}
              navigate={navigate}
            />
          )}

          {tab === 'months' && (
            <SavedMonths
              saved={data?.saved ?? []}
              current={month}
              onPick={(m) => {
                setMonth(m);
                setTab('strategy');
              }}
              navigate={navigate}
            />
          )}
        </>
      )}
    </>
  );
}

// ============================================
// THE ONE BUTTON THAT CREATES ANYTHING
// ============================================

/**
 * Kept as its own bar rather than a button inside a card, because it is the
 * only control on the screen that spends real money and creates real posts,
 * and it should not look like the others.
 */
function GenerateBar({ data, writing, onGenerate, navigate }) {
  const { strategy, plan, posts, spend } = data;
  const nearCap = spend.capPence > 0 && spend.pence / spend.capPence > 0.8;

  if (strategy.generatedAt && posts.length > 0) {
    return (
      <div className="generate-bar generate-bar--done">
        <span className="tile tile--sm tile--ok">
          <Layers size={18} />
        </span>
        <div>
          <div className="row__label">{posts.length} drafts written for this month</div>
          <div className="row__sub">
            Nothing is scheduled. Approve them on the Drafts screen, or open the campaign to
            approve the whole month at once.
          </div>
        </div>
        <div className="generate-bar__actions">
          <button className="btn" onClick={() => navigate('/drafts')}>
            Open drafts
          </button>
          <button
            className="btn btn--primary"
            onClick={() => navigate(`/campaigns/${strategy.campaignId}`)}
          >
            <Calendar size={16} /> Review the month
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="generate-bar">
      <span className="tile tile--sm">
        <Sparkle size={18} />
      </span>
      <div>
        <div className="row__label">Ready to write {plan.length} posts</div>
        <div className="row__sub">
          Copilot writes every caption and puts each post in Drafts. Nothing is scheduled and
          nothing is published.
          {nearCap && ' You are close to this month’s AI spend cap.'}
        </div>
      </div>
      <div className="generate-bar__actions">
        <button
          className="btn btn--primary"
          onClick={onGenerate}
          disabled={writing || plan.length === 0}
        >
          <Sparkle size={16} /> {writing ? 'Writing the month…' : 'Generate the month'}
        </button>
      </div>
    </div>
  );
}

// ============================================
// SAVED MONTHS
// ============================================

function SavedMonths({ saved, current, onPick, navigate }) {
  return (
    <section className="card">
      <div className="card__head">
        <span className="tile">
          <Table size={21} />
        </span>
        <div className="card__head-text">
          <h2>Saved months</h2>
          <p>Every month this brand has planned.</p>
        </div>
      </div>

      {saved.length === 0 ? (
        <div className="row">
          <span className="tile tile--sm">
            <Sparkle size={17} />
          </span>
          <div>
            <div className="row__label">Nothing saved yet</div>
            <div className="row__sub">Generate a strategy and it is kept here.</div>
          </div>
        </div>
      ) : (
        <div className="rows">
          {saved.map((row) => (
            <div className="row" key={row.id}>
              <span className="tile tile--sm">
                <Calendar size={17} />
              </span>
              <div>
                <div className="row__label">
                  {row.month}
                  {row.month === current && <span className="tag tag--muted">Showing</span>}
                </div>
                <div className="row__sub">
                  {row.goal || 'No objective recorded'} ·{' '}
                  {row.generated_at
                    ? `${row.drafts} drafts written`
                    : 'strategy only, nothing written'}
                </div>
              </div>
              <div className="row__end">
                <button className="btn btn--sm" onClick={() => onPick(row.month)}>
                  Open
                </button>
                {row.campaign_id && (
                  <button
                    className="btn btn--sm"
                    onClick={() => navigate(`/campaigns/${row.campaign_id}`)}
                  >
                    Campaign
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
